import { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import ReactApexChart from "react-apexcharts";

import 'assets/scss/kpi.scss';
import 'assets/scss/pulse.scss';
import { currencyFormatter } from "components/KPIs/helpers";
import BlankSpace from "components/UI/Atoms/BlankCell";
import SearchableMultiSelect from "components/UI/Atoms/inputs/selects/searchableMultiselect";
import { Collapse } from "react-bootstrap";
import toast, { Toaster } from "react-hot-toast";
import { fetchBillingPeriod, setGoalDraft, updateGoal } from "redux/KPIs/KPIsSlice";
import { fetchBillingPeriodOptions } from "redux/KPIs/KPIsSlice";
import { useDebouncedValue } from "utils/logic";

const KPIs = () => {
    const dispatch = useDispatch();
    const [focusedPeriodId, setFocusedPeriodId] = useState(null);
    const [showAllTopCustomers, setShowAllTopCustomers] = useState(false);
    const [focusedGoalCell, setFocusedGoalCell] = useState(null);
    const [pendingGoalUpdate, setPendingGoalUpdate] = useState(null);
    const { data: billingPeriod, loading: billingPeriodLoading } = useSelector((state) => state.KPIs.billingPeriod);
    const { data: billingPeriodOptionsData, loading: billingPeriodOptionsLoading } = useSelector(state => state.KPIs.billingPeriodOptions);
    const goalDrafts = useSelector((state) => state.KPIs.goalDrafts);
    const savingGoalByCell = useSelector((state) => state.KPIs.savingGoalByCell);
    const debouncedGoalUpdate = useDebouncedValue(pendingGoalUpdate, 400);

    const isCurrencyGoal = (goalType) => {
        const currencyMetrics = billingPeriod?.goal_setting_map?.currency_metrics || ["revenue", "profit"];
        return currencyMetrics.includes(goalType);
    };

    const parseGoalNumber = (value) => {
        if (value === null || value === undefined || value === "") return null;
        const normalized = String(value).replace(/[$,\s]/g, "");
        const num = Number(normalized);
        return Number.isFinite(num) ? num : null;
    };
    const getDraftKey = (scope, id) => `${scope}:${id}`;
    const resolvedBillingPeriodId =
        billingPeriod?.id
        ?? billingPeriod?.billing_period?.id
        ?? billingPeriod?.billing_period_id
        ?? null;

    useEffect(() => {
        dispatch(fetchBillingPeriodOptions());
    }, [dispatch]);

    useEffect(() => {
        if (focusedPeriodId !== null) {
            dispatch(fetchBillingPeriod(focusedPeriodId));
        } else {
            dispatch(fetchBillingPeriod());
        }
    }, [dispatch, focusedPeriodId]);

    useEffect(() => {
        if (!debouncedGoalUpdate) return;
        dispatch(updateGoal(debouncedGoalUpdate))
            .unwrap()
            .then(() => { })
            .catch((error) => {
                const detail =
                    error?.detail ||
                    error?.message ||
                    "Failed to update goal";
                toast.error(detail);
            });
    }, [debouncedGoalUpdate, dispatch]);

    const onGoalDraftChange = (draftKey, goalType, value) => {
        dispatch(setGoalDraft({ draftKey, goalType, value }));
    };

    const renderGoalInput = (item, goalType, scope = "customer", persist = true) => {
        const itemId = item?.id;
        const draftKey = getDraftKey(scope, itemId);
        const cellKey = `${draftKey}:${goalType}`;
        const isFocused = focusedGoalCell === cellKey;
        const rawValue = goalDrafts?.[draftKey]?.[goalType] ?? "";
        const parsedValue = parseGoalNumber(rawValue);
        const displayValue = parsedValue !== null
            ? (isCurrencyGoal(goalType) ? currencyFormatter.format(parsedValue) : String(parsedValue))
            : (isCurrencyGoal(goalType) ? currencyFormatter.format(0) : "0");
        const saving = !!savingGoalByCell[cellKey];

        if (!isFocused) {
            return (
                <div className="kpi-goal-field">
                    <button
                        type="button"
                        className="kpi-goal-affordance kpi-goal-affordance--action"
                        title="Edit goal"
                        aria-label={`Edit ${goalType} goal`}
                        onClick={() => setFocusedGoalCell(cellKey)}
                    >
                        ✎
                    </button>
                    <button
                        type="button"
                        className="kpi-goal-display"
                        title="Click to edit goal"
                        aria-label={`Edit ${goalType} goal`}
                        onClick={() => setFocusedGoalCell(cellKey)}
                    >
                        <span className="kpi-goal-display__value">{displayValue}</span>
                    </button>
                    {saving && <span className="kpi-goal-saving">Saving...</span>}
                </div>
            );
        }

        return (
            <div className="kpi-goal-field">
                <input
                    className="kpi-goal-input"
                    type="text"
                    inputMode="decimal"
                    autoFocus
                    value={rawValue}
                    onChange={(event) => {
                        const nextValue = event.target.value;
                        onGoalDraftChange(draftKey, goalType, nextValue);
                        if (persist) {
                            const parsedValue = parseGoalNumber(nextValue);
                            if (nextValue === "" || parsedValue !== null) {
                                setPendingGoalUpdate({ scope, item, goalType, value: nextValue });
                            }
                        }
                    }}
                    onBlur={() => {
                        setFocusedGoalCell(null);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.currentTarget.blur();
                        }
                    }}
                />
                {saving && <span className="kpi-goal-saving">Saving...</span>}
            </div>
        );
    };

    const renderActualGoal = (actualValue, goalInput) => (
        <div className="kpi-goal-pair">
            <span className="kpi-goal-pair__actual">{actualValue}</span>
            <span className="kpi-goal-pair__sep">/</span>
            <span className="kpi-goal-pair__goal">{goalInput}</span>
        </div>
    );

    const getDeltaPercent = (current, previous) => {
        if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null;
        return ((current - previous) / Math.abs(previous)) * 100;
    };

    const renderTableTrend = (current, previous) => {
        const delta = getDeltaPercent(current, previous);
        if (delta === null) {
            return <span className="kpi-table-trend is-neutral">—</span>;
        }
        if (Math.abs(delta) < 0.0001) {
            return <span className="kpi-table-trend is-neutral">0.0%</span>;
        }
        const isUp = delta >= 0;
        return (
            <span className={`kpi-table-trend ${isUp ? "is-up" : "is-down"}`}>
                <i className={`fa ${isUp ? "fa-arrow-up" : "fa-arrow-down"}`} />
                {Math.abs(delta).toFixed(1)}%
            </span>
        );
    };

    const renderActualWithTrend = (actualDisplay, current, previous) => (
        <span className="kpi-actual-with-trend">
            {renderTableTrend(current, previous)}
            <span>{actualDisplay}</span>
        </span>
    );

    const pickFiniteNumber = (...values) => {
        for (const value of values) {
            const num = Number(value);
            if (Number.isFinite(num)) return num;
        }
        return null;
    };

    const getTrendValues = (item, metric) => {
        if (metric === "loads") {
            return {
                current: pickFiniteNumber(item?.current_orders, item?.orders, item?.current_loads, item?.loads),
                previous: pickFiniteNumber(item?.previous_orders, item?.previous_loads),
            };
        }
        if (metric === "revenue") {
            return {
                current: pickFiniteNumber(item?.current_revenue, item?.revenue),
                previous: pickFiniteNumber(item?.previous_revenue),
            };
        }
        if (metric === "profit") {
            return {
                current: pickFiniteNumber(item?.current_profit, item?.profit),
                previous: pickFiniteNumber(item?.previous_profit),
            };
        }
        if (metric === "margin") {
            return {
                current: pickFiniteNumber(item?.current_margin_percentage, item?.margin_percentage),
                previous: pickFiniteNumber(item?.previous_margin_percentage),
            };
        }
        return { current: null, previous: null };
    };

    const normalizeLabel = (label) => {
        if (label === null || label === undefined) return null;
        const text = String(label).trim();
        if (!text) return null;
        if (text.toLowerCase() === "none") return null;
        return text;
    };

    const getTableLabel = (label) => normalizeLabel(label);
    const getChartLabel = (label) => normalizeLabel(label) || "Others";

    const getGoalMarginDisplay = (item, scope = "customer") => {
        const draftKey = getDraftKey(scope, item?.id);
        const draft = goalDrafts?.[draftKey] || {};
        const revenueGoal = parseGoalNumber(draft.revenue);
        const profitGoal = parseGoalNumber(draft.profit);
        if (!revenueGoal || revenueGoal <= 0 || profitGoal === null) return "—";
        return `${((profitGoal / revenueGoal) * 100).toFixed(1)}%`;
    };

    const getActualMarginDisplay = (item) => {
        if (item?.margin_percentage != null) {
            return `${Number(item.margin_percentage).toFixed(1)}%`;
        }
        const revenue = Number(item?.revenue) || 0;
        const profit = Number(item?.profit) || 0;
        if (revenue <= 0) return "—";
        return `${((profit / revenue) * 100).toFixed(1)}%`;
    };

    const companyActualLoads = Number(billingPeriod?.company?.orders) || 0;
    const companyActualRevenue = Number(billingPeriod?.company?.revenue) || 0;
    const companyActualProfit = Number(billingPeriod?.company?.profit) || 0;
    const companyPreviousLoadsRaw = Number(billingPeriod?.company?.previous_orders);
    const companyPreviousRevenueRaw = Number(billingPeriod?.company?.previous_revenue);
    const companyPreviousProfitRaw = Number(billingPeriod?.company?.previous_profit);
    const companyGoalItem = {
        id: billingPeriod?.company?.id || "company",
        label: billingPeriod?.company?.label || "Company",
        scope: "company",
    };
    const companyActualMargin = billingPeriod?.company?.margin_percentage != null
        ? Number(billingPeriod.company.margin_percentage)
        : (companyActualRevenue > 0 ? (companyActualProfit / companyActualRevenue) * 100 : null);
    const companyDraftKey = getDraftKey("company", companyGoalItem.id);
    const companyDraft = goalDrafts?.[companyDraftKey] || {};
    const companyGoalTotals = {
        loads: parseGoalNumber(companyDraft.loads) ?? 0,
        revenue: parseGoalNumber(companyDraft.revenue) ?? 0,
        profit: parseGoalNumber(companyDraft.profit) ?? 0,
    };
    const companyGoalMargin = companyGoalTotals.revenue > 0
        ? (companyGoalTotals.profit / companyGoalTotals.revenue) * 100
        : null;
    const companyPreviousLoads = Number.isFinite(companyPreviousLoadsRaw) ? companyPreviousLoadsRaw : null;
    const companyPreviousRevenue = Number.isFinite(companyPreviousRevenueRaw) ? companyPreviousRevenueRaw : null;
    const companyPreviousProfit = Number.isFinite(companyPreviousProfitRaw) ? companyPreviousProfitRaw : null;
    const companyPreviousMarginRaw = Number(billingPeriod?.company?.previous_margin_percentage);
    const companyPreviousMargin = Number.isFinite(companyPreviousMarginRaw) ? companyPreviousMarginRaw : null;

    const renderCompanyTrend = (current, previous) => {
        const delta = getDeltaPercent(current, previous);
        if (delta === null) {
            return <span className="kpi-company-trend is-neutral">—</span>;
        }
        if (Math.abs(delta) < 0.0001) {
            return <span className="kpi-company-trend is-neutral">0.0%</span>;
        }

        const isUp = delta >= 0;
        return (
            <span className={`kpi-company-trend ${isUp ? "is-up" : "is-down"}`}>
                <i className={`fa ${isUp ? "fa-arrow-up" : "fa-arrow-down"}`} />
                {Math.abs(delta).toFixed(1)}%
            </span>
        );
    };

    const toCompletionPct = (actual, goal) => {
        if (!goal || goal <= 0) return 0;
        return Math.max(0, Number(((actual / goal) * 100).toFixed(1)));
    };

    const companyCompletion = [
        {
            key: "loads",
            label: "Loads",
            actual: companyActualLoads,
            goal: companyGoalTotals.loads,
            completion: toCompletionPct(companyActualLoads, companyGoalTotals.loads),
            displayActual: Math.round(companyActualLoads).toLocaleString(),
            displayGoal: Math.round(companyGoalTotals.loads).toLocaleString(),
        },
        {
            key: "revenue",
            label: "Revenue",
            actual: companyActualRevenue,
            goal: companyGoalTotals.revenue,
            completion: toCompletionPct(companyActualRevenue, companyGoalTotals.revenue),
            displayActual: currencyFormatter.format(companyActualRevenue),
            displayGoal: currencyFormatter.format(companyGoalTotals.revenue),
        },
        {
            key: "profit",
            label: "Profit",
            actual: companyActualProfit,
            goal: companyGoalTotals.profit,
            completion: toCompletionPct(companyActualProfit, companyGoalTotals.profit),
            displayActual: currencyFormatter.format(companyActualProfit),
            displayGoal: currencyFormatter.format(companyGoalTotals.profit),
        },
        {
            key: "margin",
            label: "Margin",
            actual: companyActualMargin ?? 0,
            goal: companyGoalMargin ?? 0,
            completion: toCompletionPct(companyActualMargin ?? 0, companyGoalMargin ?? 0),
            displayActual: companyActualMargin != null ? `${companyActualMargin.toFixed(1)}%` : "—",
            displayGoal: companyGoalMargin != null ? `${companyGoalMargin.toFixed(1)}%` : "—",
        },
    ];

    const renderScopeCompletion = (metrics, options = {}) => (
        <div className="kpi-table-completion">
            {metrics.map((metric) => (
                <div className={`kpi-table-completion-item ${options.hideLabels ? "is-compact" : ""}`} key={metric.key}>
                    {options.hideLabels ? null : (
                        <div className="kpi-table-completion-head">
                            <span>{metric.label}</span>
                        </div>
                    )}
                    <div className="kpi-table-completion-track">
                        <span
                            className="kpi-table-completion-fill"
                            style={{ width: `${Math.min(Math.max(metric.completion, 0), 100)}%` }}
                        />
                    </div>
                    <div className="kpi-table-completion-values">
                        <span>{metric.displayActual}</span>
                        <span>/</span>
                        <span>{metric.displayGoal}</span>
                        <strong>{metric.completion.toFixed(1)}%</strong>
                    </div>
                </div>
            ))}
        </div>
    );

    const companyGoalsByMetric = {
        orders: companyGoalTotals.loads,
        revenue: companyGoalTotals.revenue,
        profit: companyGoalTotals.profit,
    };

    const normalizedContributionAxisMax = (() => {
        const scopes = [
            billingPeriod?.customer_segmentation || [],
            billingPeriod?.team_member_contributions || [],
            billingPeriod?.customers || [],
        ];
        const metricKeys = ["orders", "revenue", "profit"];

        let maxPct = 0;
        scopes.forEach((scopeItems) => {
            metricKeys.forEach((metricKey) => {
                const goal = Number(companyGoalsByMetric[metricKey]) || 0;
                if (goal <= 0) return;
                const totalActual = scopeItems.reduce((sum, item) => sum + (Number(item?.[metricKey]) || 0), 0);
                maxPct = Math.max(maxPct, (totalActual / goal) * 100);
            });
        });

        if (maxPct <= 0) return 100;
        return Math.max(100, Math.ceil(maxPct / 25) * 25);
    })();

    const getContributionChartConfig = (items = [], getLabel) => {
        const metrics = [
            { key: "orders", label: "Loads" },
            { key: "revenue", label: "Revenue" },
            { key: "profit", label: "Profit" },
        ];

        const contributionByName = {};
        items.forEach((item, idx) => {
            const name = getLabel(item, idx);
            if (!contributionByName[name]) {
                contributionByName[name] = [0, 0, 0];
            }
            metrics.forEach((metric, metricIndex) => {
                const goal = Number(companyGoalsByMetric[metric.key]) || 0;
                const value = Number(item?.[metric.key]) || 0;
                contributionByName[name][metricIndex] += goal > 0 ? (value / goal) * 100 : 0;
            });
        });

        const contributors = Object.entries(contributionByName)
            .map(([name, shares]) => ({
                name,
                shares,
                score: shares[0] + shares[1] + shares[2],
            }))
            .filter((item) => item.score > 0)
            .sort((a, b) => b.score - a.score);

        const maxContributors = 10;
        const top = contributors.slice(0, maxContributors);
        const remainder = contributors.slice(maxContributors);
        const othersShares = remainder.reduce((acc, item) => {
            acc[0] += item.shares[0];
            acc[1] += item.shares[1];
            acc[2] += item.shares[2];
            return acc;
        }, [0, 0, 0]);

        const series = top.map((item) => ({
            name: item.name,
            data: item.shares.map((value) => Number(value.toFixed(2))),
        }));
        if (othersShares.some((value) => value > 0)) {
            series.push({
                name: "Others",
                data: othersShares.map((value) => Number(value.toFixed(2))),
            });
        }

        return {
            hasData: series.length > 0,
            series,
            options: {
                chart: {
                    type: "bar",
                    stacked: true,
                    toolbar: { show: false },
                    animations: { enabled: false },
                },
                legend: {
                    position: "top",
                    horizontalAlign: "right",
                    fontSize: "10px",
                    itemMargin: {
                        horizontal: 8,
                        vertical: 0,
                    },
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: "46%",
                        borderRadius: 2,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                xaxis: {
                    categories: metrics.map((metric) => metric.label),
                    min: 0,
                    max: normalizedContributionAxisMax,
                    tickAmount: 10,
                    title: { text: "Company Goal Completion %" },
                    labels: {
                        formatter: (value) => `${Math.round(Number(value) || 0)}%`,
                    },
                },
                yaxis: {
                    labels: {
                        style: { fontSize: "12px", fontWeight: 600 },
                    },
                },
                grid: {
                    borderColor: "rgba(0,0,0,0.08)",
                    xaxis: { lines: { show: true } },
                    yaxis: { lines: { show: true } },
                    padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                    },
                },
                tooltip: {
                    y: {
                        formatter: (value) => `${Number(value).toFixed(1)}% of company goal`,
                    },
                },
            },
        };
    };

    const segmentContributionChart = getContributionChartConfig(
        billingPeriod?.customer_segmentation || [],
        (item) => getChartLabel(item?.label)
    );
    const memberContributionChart = getContributionChartConfig(
        billingPeriod?.team_member_contributions || [],
        (item) => getChartLabel(item?.label)
    );
    const customerContributionChart = getContributionChartConfig(
        billingPeriod?.customers || [],
        (item) => getChartLabel(item?.label)
    );

    const renderContributionChart = (chartConfig, title) => (
        <div className="kpi-contribution-chart">
            <div className="kpi-contribution-title">{title}</div>
            {chartConfig.hasData ? (
                <ReactApexChart
                    options={chartConfig.options}
                    series={chartConfig.series}
                    type="bar"
                    height={190}
                />
            ) : (
                <div className="kpi-contribution-empty">No contribution data available.</div>
            )}
        </div>
    );

    const selectedPeriodOption = (billingPeriodOptionsData || []).find(
        (option) => Number(option?.value) === Number(focusedPeriodId ?? resolvedBillingPeriodId)
    );
    const getOrdinalSuffix = (day) => {
        const normalized = Number(day);
        if (!Number.isFinite(normalized)) return "";
        const mod100 = normalized % 100;
        if (mod100 >= 11 && mod100 <= 13) return "th";
        const mod10 = normalized % 10;
        if (mod10 === 1) return "st";
        if (mod10 === 2) return "nd";
        if (mod10 === 3) return "rd";
        return "th";
    };
    const normalizeDateOrdinal = (value) => {
        if (!value || typeof value !== "string") return value;
        return value.replace(
            /\b([A-Za-z]{3,9}\s+)(\d{1,2})(?:st|nd|rd|th)?(,\s+\d{4})\b/g,
            (_, month, dayRaw, yearPart) => {
                const day = Number(dayRaw);
                if (!Number.isFinite(day)) return `${month}${dayRaw}${yearPart}`;
                return `${month}${day}${getOrdinalSuffix(day)}${yearPart}`;
            }
        );
    };
    const periodStartDate = billingPeriod?.start_date || billingPeriod?.billing_period?.start_date || null;
    const periodEndDate = billingPeriod?.end_date || billingPeriod?.billing_period?.end_date || null;
    const periodDateRange = periodStartDate && periodEndDate
        ? `${normalizeDateOrdinal(periodStartDate)} - ${normalizeDateOrdinal(periodEndDate)}`
        : normalizeDateOrdinal(selectedPeriodOption?.label || "—");
    const customerCount = Array.isArray(billingPeriod?.customers) ? billingPeriod.customers.length : 0;
    const segmentCount = Array.isArray(billingPeriod?.customer_segmentation) ? billingPeriod.customer_segmentation.length : 0;
    const memberCount = Array.isArray(billingPeriod?.team_member_contributions) ? billingPeriod.team_member_contributions.length : 0;

    const contextItems = [
        { label: "Reporting Period", value: periodDateRange, prominent: true, icon: "fa fa-calendar" },
    ];


    return (
        <>
            <Toaster position="bottom-right" />
            <div className="pulse-period-controls kpi-period-select">
                <SearchableMultiSelect
                    options={(billingPeriodOptionsData || []).map((option) => ({
                        value: option.value,
                        label: option.label,
                    }))}
                    value={focusedPeriodId ?? resolvedBillingPeriodId}
                    single={true}
                    disabled={billingPeriodOptionsLoading}
                    onChange={(id) => {
                        setFocusedPeriodId(id ?? null);
                    }}
                    maxMenuHeight={820}
                    orderOptions={null}
                    placeholder={billingPeriodOptionsLoading ? "Loading periods..." : "Select Billing Period"}
                />
                <div className="kpi-context-strip">
                    {contextItems.map((item) => (
                        <div className={`kpi-context-item${item.prominent ? " is-prominent" : ""}`} key={item.label}>
                            <span className="kpi-context-item__label">
                                {item.icon ? <i className={`kpi-context-item__icon ${item.icon}`} /> : null}
                                {item.label}
                            </span>
                            <span className="kpi-context-item__value">{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>
            <section className="pulse-section">
                <div className="pulse-header">
                    <h1><i className="fa fa-chart-line kpi-section-icon" />Executive Summary</h1>
                </div>
                <div className="pulse-body">
                    <div className="kpi-company-grid">
                        <div className="kpi-company-card in-pulse-card">
                            <div className="kpi-company-label">Loads</div>
                            <div className="kpi-company-values">
                                <div className="kpi-company-value-group">
                                    <div className="kpi-company-value-caption">Actual</div>
                                    <div className="kpi-company-value">
                                        {renderCompanyTrend(companyActualLoads, companyPreviousLoads)}
                                        <span>{companyActualLoads.toLocaleString()}</span>
                                    </div>
                                </div>
                                <div className="kpi-company-value-group kpi-company-value-group--goal">
                                    <div className="kpi-company-value-caption">Goal</div>
                                    <div className="kpi-company-goal">{renderGoalInput(companyGoalItem, "loads", "company")}</div>
                                </div>
                            </div>
                        </div>
                        <div className="kpi-company-card in-pulse-card">
                            <div className="kpi-company-label">Revenue</div>
                            <div className="kpi-company-values">
                                <div className="kpi-company-value-group">
                                    <div className="kpi-company-value-caption">Actual</div>
                                    <div className="kpi-company-value">
                                        {renderCompanyTrend(companyActualRevenue, companyPreviousRevenue)}
                                        <span>{currencyFormatter.format(companyActualRevenue)}</span>
                                    </div>
                                </div>
                                <div className="kpi-company-value-group kpi-company-value-group--goal">
                                    <div className="kpi-company-value-caption">Goal</div>
                                    <div className="kpi-company-goal">{renderGoalInput(companyGoalItem, "revenue", "company")}</div>
                                </div>
                            </div>
                        </div>
                        <div className="kpi-company-card in-pulse-card">
                            <div className="kpi-company-label">Profit</div>
                            <div className="kpi-company-values">
                                <div className="kpi-company-value-group">
                                    <div className="kpi-company-value-caption">Actual</div>
                                    <div className="kpi-company-value">
                                        {renderCompanyTrend(companyActualProfit, companyPreviousProfit)}
                                        <span>{currencyFormatter.format(companyActualProfit)}</span>
                                    </div>
                                </div>
                                <div className="kpi-company-value-group kpi-company-value-group--goal">
                                    <div className="kpi-company-value-caption">Goal</div>
                                    <div className="kpi-company-goal">{renderGoalInput(companyGoalItem, "profit", "company")}</div>
                                </div>
                            </div>
                        </div>
                        <div className="kpi-company-card in-pulse-card">
                            <div className="kpi-company-label">Margin</div>
                            <div className="kpi-company-values">
                                <div className="kpi-company-value-group">
                                    <div className="kpi-company-value-caption">Actual</div>
                                    <div className="kpi-company-value">
                                        {renderCompanyTrend(companyActualMargin, companyPreviousMargin)}
                                        <span>{companyActualMargin != null ? `${companyActualMargin.toFixed(1)}%` : "—"}</span>
                                    </div>
                                </div>
                                <div className="kpi-company-value-group kpi-company-value-group--goal">
                                    <div className="kpi-company-value-caption">Goal</div>
                                    <div className="kpi-company-goal">{companyGoalMargin != null ? `${companyGoalMargin.toFixed(1)}%` : "—"}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {renderScopeCompletion(companyCompletion, { hideLabels: true })}
                </div>
            </section>
            <hr />
            <section className="pulse-section">
                <div className="pulse-header">
                    <h1>
                        <i className="fa fa-layer-group kpi-section-icon" />
                        Customer Segments
                        <span className="kpi-section-count">{segmentCount.toLocaleString()}</span>
                    </h1>
                </div>
                <div className="pulse-body">
                    {renderContributionChart(segmentContributionChart, "Customer Segment Contribution to Company Goal")}
                    <table className="kpis-goals-table in-pulse-card balance-table striped">
                        <thead>
                            <tr>
                                <th>Segment <i className="fa fa-arrow-up"></i></th>
                                <th className="kpi-col-number">Loads</th>
                                <th className="kpi-col-number">Revenue</th>
                                <th className="kpi-col-number">Profit</th>
                                <th className="kpi-col-number">Margin</th>
                            </tr>
                        </thead>
                        <tbody>
                            {billingPeriod && billingPeriod.customer_segmentation.map((segment) => (
                                <tr key={segment.id}>
                                    <td>{getTableLabel(segment.label) || <BlankSpace />}</td>
                                    <td className="kpi-col-number">
                                        {renderActualGoal(
                                            renderActualWithTrend(
                                                segment.orders,
                                                getTrendValues(segment, "loads").current,
                                                getTrendValues(segment, "loads").previous
                                            ),
                                            renderGoalInput(segment, "loads", "segment")
                                        )}
                                    </td>
                                    <td className="kpi-col-number">
                                        {renderActualGoal(
                                            renderActualWithTrend(
                                                currencyFormatter.format(segment.revenue),
                                                getTrendValues(segment, "revenue").current,
                                                getTrendValues(segment, "revenue").previous
                                            ),
                                            renderGoalInput(segment, "revenue", "segment")
                                        )}
                                    </td>
                                    <td className="kpi-col-number">
                                        {renderActualGoal(
                                            renderActualWithTrend(
                                                currencyFormatter.format(segment.profit),
                                                getTrendValues(segment, "profit").current,
                                                getTrendValues(segment, "profit").previous
                                            ),
                                            renderGoalInput(segment, "profit", "segment")
                                        )}
                                    </td>
                                    <td className="kpi-col-number">
                                        {renderActualGoal(
                                            renderActualWithTrend(
                                                getActualMarginDisplay(segment),
                                                getTrendValues(segment, "margin").current,
                                                getTrendValues(segment, "margin").previous
                                            ),
                                            getGoalMarginDisplay(segment, "segment")
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
            <hr />
            <section className="pulse-section">
                <div className="pulse-header">
                    <h1>
                        <i className="fa fa-users kpi-section-icon" />
                        Team Members
                        <span className="kpi-section-count">{memberCount.toLocaleString()}</span>
                    </h1>
                </div>
                <div className="pulse-body">
                    {renderContributionChart(memberContributionChart, "Team Member Contribution to Company Goal")}
                    <table className="kpis-goals-table in-pulse-card balance-table striped">
                        <thead>
                            <tr>
                                <th>Team Member</th>
                                <th className="kpi-col-number">Loads</th>
                                <th className="kpi-col-number">Revenue</th>
                                <th className="kpi-col-number">Profit <i className="fa fa-arrow-up"></i></th>
                                <th className="kpi-col-number">Margin</th>
                            </tr>
                        </thead>
                        <tbody>
                            {billingPeriod && billingPeriod.team_member_contributions.map((member) => (
                                <tr key={member.id}>
                                    <td>{getTableLabel(member.label) || <BlankSpace />}</td>
                                    <td className="kpi-col-number">
                                        {renderActualGoal(
                                            renderActualWithTrend(
                                                member.orders,
                                                getTrendValues(member, "loads").current,
                                                getTrendValues(member, "loads").previous
                                            ),
                                            renderGoalInput(member, "loads", "member")
                                        )}
                                    </td>
                                    <td className="kpi-col-number">
                                        {renderActualGoal(
                                            renderActualWithTrend(
                                                currencyFormatter.format(member.revenue),
                                                getTrendValues(member, "revenue").current,
                                                getTrendValues(member, "revenue").previous
                                            ),
                                            renderGoalInput(member, "revenue", "member")
                                        )}
                                    </td>
                                    <td className="kpi-col-number">
                                        {renderActualGoal(
                                            renderActualWithTrend(
                                                currencyFormatter.format(member.profit),
                                                getTrendValues(member, "profit").current,
                                                getTrendValues(member, "profit").previous
                                            ),
                                            renderGoalInput(member, "profit", "member")
                                        )}
                                    </td>
                                    <td className="kpi-col-number">
                                        {renderActualGoal(
                                            renderActualWithTrend(
                                                getActualMarginDisplay(member),
                                                getTrendValues(member, "margin").current,
                                                getTrendValues(member, "margin").previous
                                            ),
                                            getGoalMarginDisplay(member, "member")
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
            <hr />
            <section className="pulse-section">
                <div className="pulse-header">
                    <h1>
                        <i className="fa fa-building kpi-section-icon" />
                        Top Customers
                        <span className="kpi-section-count">{customerCount.toLocaleString()}</span>
                    </h1>
                </div>
                <div className="pulse-body">
                    {renderContributionChart(customerContributionChart, "Customer Contribution to Company Goal")}
                    <table className="kpis-goals-table in-pulse-card balance-table striped">
                        <thead>
                            <tr>
                                <th>Customer</th>
                                <th className="kpi-col-number">Loads</th>
                                <th className="kpi-col-number">Revenue <i className="fa fa-arrow-down"></i></th>
                                <th className="kpi-col-number">Profit</th>
                                <th className="kpi-col-number">Margin</th>
                            </tr>
                        </thead>
                        <tbody>
                            {billingPeriod && billingPeriod.customers
                                .slice(0, 10)
                                .map((customer) => (
                                    <tr key={customer.id}>
                                        <td>{getTableLabel(customer.label) || <BlankSpace />}</td>
                                        <td className="kpi-col-number">
                                            {renderActualGoal(
                                                renderActualWithTrend(
                                                    customer.orders,
                                                    getTrendValues(customer, "loads").current,
                                                    getTrendValues(customer, "loads").previous
                                                ),
                                                renderGoalInput(customer, "loads")
                                            )}
                                        </td>
                                        <td className="kpi-col-number">
                                            {renderActualGoal(
                                                renderActualWithTrend(
                                                    currencyFormatter.format(customer.revenue),
                                                    getTrendValues(customer, "revenue").current,
                                                    getTrendValues(customer, "revenue").previous
                                                ),
                                                renderGoalInput(customer, "revenue")
                                            )}
                                        </td>
                                        <td className="kpi-col-number">
                                            {renderActualGoal(
                                                renderActualWithTrend(
                                                    currencyFormatter.format(customer.profit),
                                                    getTrendValues(customer, "profit").current,
                                                    getTrendValues(customer, "profit").previous
                                                ),
                                                renderGoalInput(customer, "profit")
                                            )}
                                        </td>
                                        <td className="kpi-col-number">
                                            {renderActualGoal(
                                                renderActualWithTrend(
                                                    getActualMarginDisplay(customer),
                                                    getTrendValues(customer, "margin").current,
                                                    getTrendValues(customer, "margin").previous
                                                ),
                                                getGoalMarginDisplay(customer, "customer")
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            {!showAllTopCustomers && (
                                <tr>
                                    <td colSpan={5} className="kpis-collapse-cell">
                                        <button
                                            className="kpis-collapse-toggle"
                                            type="button"
                                            onClick={() => setShowAllTopCustomers(true)}
                                            aria-expanded={false}
                                            title="Show full list"
                                        >
                                            <i className="fa fa-chevron-down" />
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <Collapse in={showAllTopCustomers}>
                        <div className="kpis-goals-collapse">
                            <table className="kpis-goals-table in-pulse-card balance-table striped">
                                <tbody>
                                    {billingPeriod && billingPeriod.customers
                                        .slice(10)
                                        .map((customer) => (
                                            <tr key={customer.id}>
                                                <td>{getTableLabel(customer.label) || <BlankSpace />}</td>
                                                <td className="kpi-col-number">
                                                    {renderActualGoal(
                                                        renderActualWithTrend(
                                                            customer.orders,
                                                            getTrendValues(customer, "loads").current,
                                                            getTrendValues(customer, "loads").previous
                                                        ),
                                                        renderGoalInput(customer, "loads")
                                                    )}
                                                </td>
                                                <td className="kpi-col-number">
                                                    {renderActualGoal(
                                                        renderActualWithTrend(
                                                            currencyFormatter.format(customer.revenue),
                                                            getTrendValues(customer, "revenue").current,
                                                            getTrendValues(customer, "revenue").previous
                                                        ),
                                                        renderGoalInput(customer, "revenue")
                                                    )}
                                                </td>
                                                <td className="kpi-col-number">
                                                    {renderActualGoal(
                                                        renderActualWithTrend(
                                                            currencyFormatter.format(customer.profit),
                                                            getTrendValues(customer, "profit").current,
                                                            getTrendValues(customer, "profit").previous
                                                        ),
                                                        renderGoalInput(customer, "profit")
                                                    )}
                                                </td>
                                                <td className="kpi-col-number">
                                                    {renderActualGoal(
                                                        renderActualWithTrend(
                                                            getActualMarginDisplay(customer),
                                                            getTrendValues(customer, "margin").current,
                                                            getTrendValues(customer, "margin").previous
                                                        ),
                                                        getGoalMarginDisplay(customer, "customer")
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                            <div className="kpis-collapse-end">
                                <button
                                    className="kpis-collapse-toggle"
                                    type="button"
                                    onClick={() => setShowAllTopCustomers(false)}
                                    aria-expanded={true}
                                    title="Hide full list"
                                >
                                    <i className="fa fa-chevron-up" />
                                </button>
                            </div>
                        </div>
                    </Collapse>
                </div>
            </section>
        </>
    )

    return (
        <>
            {/* <div>
                <div className="pulse-section">
                    <div className="pulse-header">
                        <h1>Customer Goals</h1>
                    </div>
                    <div className="pulse-body">
                        <table className="kpis-goals-table in-pulse-card w-100">
                            <thead>
                                <tr>
                                    <th>Customer</th>
                                    <th>Load Goal</th>
                                    <th>Loads</th>
                                    <th>Revenue Goal</th>
                                    <th>Revenue <i className="fa fa-arrow-down" title="Revenue"></i></th>
                                    <th>Profit Goal</th>
                                    <th>Profit</th>
                                    <th>Margin</th>
                                </tr>
                            </thead>
                            <tbody>
                                {billingPeriod && visibleCustomers.map((goal) => (
                                    <>
                                        <tr
                                            className={`no-shadow kpi-goals-team-row balance-subtable ${teamMembersShown[goal.id] ? 'expanded' : ''}`}
                                            key={goal.id}
                                        >
                                            <td>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                    <div style={{ flexDirection: "column" }}>
                                                        <div className="actions-cell">
                                                            <button
                                                                className="btn-xs action-btn btn-collapse"
                                                                onClick={() => {
                                                                    setTeamMembersShown({
                                                                        ...teamMembersShown,
                                                                        [goal.id]: !teamMembersShown[goal.id],
                                                                    });
                                                                }}
                                                                title="Toggle Members"
                                                            >
                                                                {teamMembersShown[goal.id] ? (
                                                                    <i className="fa fa-chevron-up"></i>
                                                                ) : (
                                                                    <i className="fa fa-chevron-down"></i>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div style={{ flexDirection: "column", justifyContent: "start", flexGrow: 1 }}>
                                                        <div className="team-name">
                                                            {`${goal.customer_name} [${goal.team_contributions.length}]`}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {
                                                    (() => {
                                                        const goalField = debouncedGoals[goal.id];
                                                        const goalType = "loads";
                                                        const goalValue = goalField ? goalField[goalType] : null;
                                                        return (
                                                            <input
                                                                className="input thin"
                                                                defaultValue={goalValue}
                                                                onChange={
                                                                    (event) => {
                                                                        BillingStatsApi
                                                                            .updateCustomerGoal(
                                                                                billingPeriod?.billing_period?.id,
                                                                                goal.id,
                                                                                { 'goal_type': goalType, 'goal_value': event.target.value }
                                                                            );
                                                                    }
                                                                }
                                                            />
                                                        )
                                                    })()
                                                }
                                            </td>
                                            <td>{goal.loads}</td>
                                            <td>
                                                {
                                                    (() => {
                                                        const goalField = debouncedGoals[goal.id];
                                                        const goalType = "revenue";
                                                        const goalValue = goalField ? goalField[goalType] : null;
                                                        return (
                                                            <input
                                                                className="input thin"
                                                                defaultValue={goalValue}
                                                                onChange={
                                                                    (event) => {
                                                                        BillingStatsApi
                                                                            .updateCustomerGoal(
                                                                                billingPeriod?.billing_period?.id,
                                                                                goal.id,
                                                                                { 'goal_type': goalType, 'goal_value': event.target.value }
                                                                            );
                                                                    }
                                                                }
                                                            />
                                                        );
                                                    })()
                                                }
                                            </td>
                                            <td>{currencyFormatter.format(goal.revenue)}</td>
                                            <td>
                                                {
                                                    (() => {
                                                        const goalField = debouncedGoals[goal.id];
                                                        const goalType = "profit";
                                                        const goalValue = goalField ? goalField[goalType] : null;
                                                        return (
                                                            <input
                                                                className="input thin"
                                                                defaultValue={goalValue}
                                                                onChange={
                                                                    (event) => {
                                                                        BillingStatsApi
                                                                            .updateCustomerGoal(
                                                                                billingPeriod?.billing_period?.id,
                                                                                goal.id,
                                                                                { 'goal_type': goalType, 'goal_value': event.target.value }
                                                                            );
                                                                    }
                                                                }
                                                            />
                                                        );
                                                    })()
                                                }
                                            </td>
                                            <td>{currencyFormatter.format(goal.profit)}</td>
                                            <td>
                                                {
                                                    goal.margin_percentage
                                                        ? `${goal.margin_percentage.toFixed()}%`
                                                        : <BlankSpace />
                                                }
                                            </td>
                                        </tr>
                                        <tr className={`balance-subtable team-members ${teamMembersShown[goal.id] ? 'expanded' : ''}`}>
                                            <td colSpan={8} className="p-0 ignore">
                                                <Collapse isOpen={teamMembersShown[goal.id]}>
                                                    <table className="w-100">
                                                        <tbody>
                                                            <tr>
                                                                <td colSpan={8} className="team-name-row">
                                                                    <p>{goal.customer_name} Contacts:</p>
                                                                </td>
                                                            </tr>
                                                            {goal.team_contributions.map((member, index) => (
                                                                <tr className={`balance-subtable team-members`}>
                                                                    <td style={{ paddingLeft: 14 }}>{member.service_team}</td>
                                                                    <td><BlankSpace /></td>
                                                                    <td>{member.loads}</td>
                                                                    <td><BlankSpace /></td>
                                                                    <td>{currencyFormatter.format(member.revenue)}</td>
                                                                    <td><BlankSpace /></td>
                                                                    <td>
                                                                        {
                                                                            member.profit
                                                                                ? currencyFormatter.format(member.profit)
                                                                                : <BlankSpace />
                                                                        }
                                                                    </td>
                                                                    <td>
                                                                        {member.revenue > 0
                                                                            ? `${((member.profit / member.revenue) * 100).toFixed()}%`
                                                                            : '---'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            <tr>
                                                                <td colSpan={8} className="text-center">
                                                                    <hr />
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </>
                                ))}
                                {billingPeriod && customers.length > 10 && (
                                    <tr className="kpi-show-more-row">
                                        <td colSpan={8} className="text-center">
                                            <button
                                                type="button"
                                                className="kpi-show-more-btn"
                                                onClick={() => setShowAllCustomers((prev) => !prev)}
                                                aria-label={showAllCustomers ? "Show top 10 customers" : `Show ${hiddenCustomersCount} more customers`}
                                            >
                                                <span className="kpi-show-more-line" aria-hidden="true"></span>
                                                <span className="kpi-show-more-icon" aria-hidden="true">
                                                    {showAllCustomers ? (
                                                        <i className="fa fa-chevron-up"></i>
                                                    ) : (
                                                        <i className="fa fa-chevron-down"></i>
                                                    )}
                                                </span>
                                            </button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <hr />
            <section className="pulse-section">
                <div className="pulse-header">
                    <h1>Team Contributions</h1>
                </div>
                <div className="pulse-body">
                    <table className="kpis-goals-table in-pulse-card w-100">
                        <thead>
                            <tr>
                                <th>Team Member</th>
                                <th>Loads</th>
                                <th>Revenue <i className="fa fa-arrow-down" title="Revenue"></i></th>
                                <th>Margin</th>
                                <th>Profit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {billingPeriod && billingPeriod.team_contributions.map((member) => (
                                <tr key={member.id}>
                                    <td>{member.name}</td>
                                    <td>{member.loads}</td>
                                    <td>{currencyFormatter.format(member.revenue)}</td>
                                    <td>
                                        {
                                            member.margin_percentage
                                                ? `${member.margin_percentage.toFixed()}%`
                                                : <BlankSpace />
                                        }
                                    </td>
                                    <td>{currencyFormatter.format(member.profit)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section> */}
        </>
    );
};

export default KPIs;
